import { cloneDeep, debounce } from 'lodash-es'
import { loadApp } from './qiankun-core'
import { MicroAppTypes } from './interface'
import { removeWatermark, setWaterMark } from '@/utils/watermark'
import { waitForAppRender, cleanupExcessApps } from './utils'

const updateMicroCache = debounce((_this: any) => {
  window.postMessage(
    {
      type: 'updateKeepAlive',
      data: {}
    },
    window.origin
  )
  _this.dispatch('microApp/checkAppStatus')
}, 400)

interface StateTypes {
  microApps: any[] // 已有的属性
  loadApps: any[]
  maxNum: number // 已有的属性
  microLoading: boolean // 添加此行，声明新属性
}

const state: StateTypes = {
  microApps: [], // 应用列表
  loadApps: [], // 已加载应用
  maxNum: 20, // 最大应用数
  microLoading: false // 初始化加载状态为false
}

const getter = {}

const mutations = {
  /**
   * 触发更新子应用keep-alive数组
   */
  updateMicroCacheList() {
    updateMicroCache(this)
  },
  // 添加加载状态更新mutation
  setMicroLoading(state: StateTypes, status: boolean) {
    console.log('first', status)
    state.microLoading = status
  }
}

const actions = {
  /**
   * 获取应用json配置
   * @param context store上下文
   * @returns Promise
   */
  getMicroConfig(context: { state: StateTypes }) {
    // 全局加水印
    let user = (window as any).localStorage.getItem('userInfo')
    user = JSON.parse(user)
    if (user?.uid && user?.name) setWaterMark(user?.uid, user?.name)

    const state: StateTypes = context.state
    return new Promise<void>((resolve, reject) => {
      const requestURL = `${process.env.BASE_URL}config.json`
      const request = new XMLHttpRequest()
      request.open('GET', requestURL)
      request.responseType = 'text'
      request.send()
      request.onload = () => {
        state.microApps = JSON.parse(request.response).map(
          (item: { container: string; name: string }) => ({
            ...item,
            container: `${item.container}-${item.name}`
          })
        )
        resolve()
      }
    })
  },
  /**
   * 加载微应用
   * @param context
   * @param routePath tab中的route，路由地址
   */
  async setMicroApp(context: any, routePath: string) {
    const state: StateTypes = context.state
    // 精确查找微应用并处理可能的undefined情况
    const microApp = state.microApps.find(item => routePath.includes(item.activeRule))
    if (!microApp) return

    // 检查应用是否已加载，避免重复加载
    const isReady = await context.dispatch('checkLoadApp', microApp)
    if (isReady) return

    try {
      context.commit('setMicroLoading', true)
      const app = await loadApp(microApp)

      // 管理应用加载队列
      state.loadApps.push({ ...cloneDeep(microApp), app })
      cleanupExcessApps(state)

      // 验证DOM渲染完成后再隐藏loading
      waitForAppRender(microApp, context, 30000)
    } catch (error) {
      context.commit('setMicroLoading', false)
      console.error('微应用加载失败:', error)
      // 可在此处添加错误上报逻辑
    }
  },

  /**
   * 检查应用是否已加载
   * @param context
   * @param microApp 子应用配置
   * @returns Promise<boolean> true 已加载 false 未加载
   */
  async checkLoadApp(context: any, microApp: MicroAppTypes): Promise<boolean> {
    // 是否在已加载应用数组里
    const isLoadIndex = state.loadApps.findIndex(item => item.name === microApp.name)
    if (isLoadIndex !== -1) {
      const appItem = state.loadApps[isLoadIndex]
      // 检查是否已渲染
      const hasHtml = document.querySelector(appItem.container)?.innerHTML
      // 注销水印
      // if (appItem.name !== 'yt-portal-tis') {
      //   removeWatermark()
      // }
      if (hasHtml) return true

      // 未渲染卸载app
      if (appItem) {
        console.log('即将卸载', appItem)
        // 使用更安全的卸载方式，添加错误处理
        if (appItem.app && typeof appItem.app.unmount === 'function') {
          try {
            console.log(
              `微应用 ${microApp.name} 状态为 ${appItem.app.getStatus?.() || 'NOT_LOADED'}`
            )
            await appItem.app.unmount()
          } catch (e) {
            console.warn(`卸载应用 ${microApp.name} 时出错:`, e)
          }
        }
        state.loadApps.splice(isLoadIndex, 1)
        return false
      }
    }
    return false
  },
  /**
   * 检查tab中的应用状态，将未在tab中打开的应用销毁
   */
  checkAppStatus() {
    state.loadApps.forEach(async item => {
      const isActive = (this as any).state.menu.tabList.some((tab: { routeUrl: string }) =>
        tab.routeUrl.includes(item.activeRule)
      )
      if (!isActive) {
        // 添加更安全的状态检查和卸载方式
        if (
          item.app &&
          typeof item.app.getStatus === 'function' &&
          item.app.getStatus() === 'MOUNTED'
        ) {
          if (typeof item.app.unmount === 'function') {
            try {
              await item.app.unmount()
            } catch (e) {
              console.warn(`卸载应用 ${item.name} 时出错:`, e)
            }
          }
          item.app = null
        }
      }
    })
    state.loadApps = state.loadApps.filter(item => item.app)
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getter
}
